import { cwd } from 'node:process';
import React from 'react';
import type { ReactElement } from 'react';
import { AuthProvider } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { render } from '@wix/cli-ui-kit';
import { DebugLogProvider } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import { BiProvider } from '@wix/cli-telemetry-react';
import { addUserFields, reportCommandStartEvent } from '@wix/cli-telemetry';
import type { Flow } from '@wix/cli-telemetry';
import type { CommandServices } from '@wix/cli-command-contract';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import type { Command } from '@commander-js/extra-typings';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import type { ProjectModel } from '../model.js';
import { loadProjectModel, ProjectModelProvider } from '../model.js';

function getFlow(model: ProjectModel): Flow {
  return model.config.projectType === 'App' ? 'Astro_App' : 'Headless';
}

async function initServices({
  model,
  command,
  flow,
  cliVersion,
  services,
}: {
  model: ProjectModel;
  command: Command;
  flow: Flow;
  cliVersion: string;
  services: CommandServices;
}) {
  const { biLogger, errorReporter, panorama } = services;

  errorReporter.setTag('project', flow);
  errorReporter.setTag('project.app_id', model.config.appId);

  panorama.setDefaultClientData({
    cliProject: flow,
    cliAppId: model.config.appId,
  });

  if (model.config.projectType === 'App' && model.config.projectId != null) {
    errorReporter.setTag('project.id', model.config.projectId);
    panorama.setDefaultClientData({ cliProjectId: model.config.projectId });
  }

  if (model.config.projectType === 'Site') {
    errorReporter.setTag('project.site_id', model.config.siteId);
    panorama.setDefaultClientData({ cliSiteId: model.config.siteId });
  }

  biLogger.updateDefaults({
    flow,
    cliVersion,
    command: command.name(),
    _appId: model.config.appId,
  });
}

export async function renderCommand(
  command: Command,
  services: CommandServices,
  cliVersion: string,
  tree: ReactElement
) {
  await runCommand(command, services, cliVersion, async (model) => {
    const { biLogger, errorReporter, panorama } = services;

    const debugLogFilePath = getDebugLogFilePath(model.projectFolder);

    const handleLogin = (userInfo: UserInfo) => {
      errorReporter.setUser({ id: userInfo.userId });
      addUserFields(biLogger, userInfo.userId);
    };

    const headers = buildHttpClientHeaders({
      flow: getFlow(model),
      command: command.name(),
      cliVersion,
    });

    await render(
      <BiProvider value={biLogger}>
        <ErrorReporterProvider value={errorReporter}>
          <PanoramaProvider value={panorama}>
            <AuthProvider onLogin={handleLogin}>
              <HttpClientProvider headers={headers}>
                <DebugLogProvider filePath={debugLogFilePath}>
                  <ProjectModelProvider value={model}>
                    {tree}
                  </ProjectModelProvider>
                </DebugLogProvider>
              </HttpClientProvider>
            </AuthProvider>
          </PanoramaProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
  });
}

export async function runCommand(
  command: Command,
  services: CommandServices,
  cliVersion: string,
  commandFn: (model: ProjectModel) => Promise<void>
) {
  const model = await loadProjectModel(cwd());
  const flow = getFlow(model);

  await initServices({ model, command, flow, services, cliVersion });

  const { panorama, biLogger } = services;

  const commandStatus = reportCommandStartEvent({
    biLogger,
    command,
    cliVersion,
    flow,
    extensions: {},
    extraFields: undefined,
  });

  const transaction = createCommandTransaction(panorama, command);

  try {
    transaction.start();
    await commandFn(model);
    transaction.finish();
    commandStatus.succeed();
  } catch (e) {
    commandStatus.failed(e);
    throw e;
  }
}
